import { FormEvent, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import { Plus } from "lucide-react";

import { ApiError, isNotFound } from "@/api/client";
import {
  getMeasurementPreset,
  getMeasurementPresetHistory,
  getMeasurementPresetVersion,
  listMeasurementPresets,
  presetKeys,
  putMeasurementPreset,
  type MeasurementPreset,
  type MeasurementPresetVersion,
  type PresetFieldError,
  type PutMeasurementPresetInput,
} from "@/api/measurementPresets";
import { Field, Input, Select, Textarea } from "@/components/form";
import { MutationError, RefreshFailed, isLoadingOrPaused, loadFailure } from "@/components/Spinner";
import {
  Button,
  ButtonLink,
  EmptyState,
  FormActionBar,
  FormPlaceholder,
  Notice,
  Page,
  PageHeader,
  Table,
  type TableColumn,
} from "@/components/ui";
import { useDirtyValues } from "@/hooks/useDirtyValues";
import { useRequestLeave, useUnsavedChanges } from "@/hooks/useUnsavedChanges";
import { formatDateTime } from "@/lib/dates";

const LIST = "/measurement-presets";

/** Mirrors ``MEASUREMENT_PRESET_ID_RE`` server-side. */
const PRESET_ID_PATTERN = "[a-z][a-z0-9\\-]{1,31}";

const FORMAT_OPTIONS = [
  { value: "acpm", label: "acpm" },
  { value: "parquet", label: "parquet" },
];

// Gabarit neutre d'un nouveau preset (spec §4.4 : « le GUI les pré-remplit
// depuis un gabarit neutre ») : toutes les règles, aucune optionnelle, l'IP
// absente du livrable. L'éditeur l'adapte avant de publier.
const NEUTRAL_RULES = {
  rules_version: 2,
  territory: ["FR"],
  live: { max_delay_ms: 30_000 },
  session: { key: "sid", client_from: "first_segment", gap_ms: 30_000, active_min_ms: 30_000, active_max_ms: 82_800_000 },
  day: { timezone: "Europe/Paris", close_local_time: "06:00", close_lag_days: [2], provisional: false },
  classification: { source: "pulsestation" },
  nomenclature: "generic-v1",
  ip: { truncate_v4: 24, truncate_v6: 48, hash: "none", expose: [] },
  metrics: "acpm-v1",
  // Bloc ads (rules_version 2) : valeurs de départ, à adapter avant de publier.
  ads: { mid_roll: "count", pre_roll: "exclude", max_break_ms: 180_000 },
};

const pretty = (value: unknown) => JSON.stringify(value, null, 2);

/** What the editor shows: text fields, so that what is typed is kept even when it does not parse yet. */
interface FormValues {
  presetId: string;
  name: string;
  description: string;
  exportFormat: MeasurementPreset["exportFormat"];
  minIpRetentionDays: string;
  /** Comma- or space-separated; empty = every tenant. */
  allowedTenants: string;
  rules: string;
  activationParams: string;
}

function valuesOf(preset: MeasurementPreset | null): FormValues {
  if (!preset) {
    return {
      presetId: "",
      name: "",
      description: "",
      exportFormat: "parquet",
      minIpRetentionDays: "15",
      allowedTenants: "",
      rules: pretty(NEUTRAL_RULES),
      activationParams: pretty({}),
    };
  }
  return {
    presetId: preset.presetId,
    name: preset.name,
    description: preset.description,
    exportFormat: preset.exportFormat,
    minIpRetentionDays: String(preset.minIpRetentionDays),
    allowedTenants: (preset.allowedTenants ?? []).join(", "),
    rules: pretty(preset.rules),
    activationParams: pretty(preset.activationParams),
  };
}

/** A JSON object typed in a textarea, or why it is not one. */
function parseObject(text: string, what: string): { value: Record<string, unknown> } | { error: string } {
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch (e) {
    return { error: `The ${what} are not valid JSON: ${e instanceof Error ? e.message : String(e)}` };
  }
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return { error: `The ${what} must be a JSON object.` };
  }
  return { value: value as Record<string, unknown> };
}

const tenantsOf = (text: string): string[] | null => {
  const ids = text.split(/[\s,]+/).filter((id) => id !== "");
  return ids.length === 0 ? null : ids;
};

/** The refused fields of a 400 (``details.fields``), or `undefined` for any other failure. */
function fieldErrorsOf(error: unknown): PresetFieldError[] | undefined {
  if (!(error instanceof ApiError) || error.status !== 400) return undefined;
  const fields = (error.body as { details?: { fields?: unknown } } | null)?.details?.fields;
  if (!Array.isArray(fields) || fields.length === 0) return undefined;
  return fields.filter(
    (f): f is PresetFieldError => typeof f?.field === "string" && typeof f?.message === "string",
  );
}

// -- the list ----------------------------------------------------------------------------------------------------------

export function MeasurementPresetsPage() {
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";
  const query = useQuery({
    queryKey: presetKeys.list,
    queryFn: () => listMeasurementPresets(token),
    enabled: !!token,
  });

  const columns: TableColumn<MeasurementPreset>[] = [
    {
      key: "name",
      header: "Name",
      cell: (p) => (
        <Link to={`${LIST}/${encodeURIComponent(p.presetId)}`} className="text-brand-600 hover:underline">
          {p.name}
        </Link>
      ),
    },
    { key: "id", header: "ID", mono: true, cell: (p) => p.presetId },
    { key: "version", header: "Version", align: "right", mono: true, cell: (p) => String(p.version) },
    { key: "format", header: "Format", mono: true, cell: (p) => p.exportFormat },
    { key: "retention", header: "Minimum IP retention", align: "right", nowrap: true, cell: (p) => `${p.minIpRetentionDays} days` },
    {
      key: "tenants",
      header: "Allowed tenants",
      wide: true,
      cell: (p) => (p.allowedTenants === null ? "All tenants" : <span className="font-mono">{p.allowedTenants.join(", ")}</span>),
    },
    { key: "updated", header: "Updated", mono: true, muted: true, nowrap: true, cell: (p) => formatDateTime(p.updatedAt) },
  ];

  return (
    <Page>
      <PageHeader
        title="Measurement presets"
        subtitle="Versioned audience measurement rules. A stage with the Audience Measurement option activates presets; each publication is pushed to those stages within minutes."
        actions={
          <ButtonLink to={`${LIST}/new`} variant="primary" icon={Plus}>
            New preset
          </ButtonLink>
        }
      />
      <RefreshFailed query={query} className="mb-4" />
      <Table
        label="Measurement presets"
        columns={columns}
        rows={query.data?.items}
        rowKey={(p) => p.presetId}
        error={loadFailure(query)}
        empty={<EmptyState title="No measurement presets yet">Create one, then activate it on a stage.</EmptyState>}
      />
    </Page>
  );
}

// -- the editor --------------------------------------------------------------------------------------------------------

function Editor({ preset, token }: { preset: MeasurementPreset | null; token: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const isNew = preset === null;
  const [values, setValues] = useState<FormValues>(() => valuesOf(preset));
  const { dirty, reset: markSaved } = useDirtyValues(values);
  const guard = useUnsavedChanges(dirty);
  const requestLeave = useRequestLeave();

  const rules = useMemo(() => parseObject(values.rules, "rules"), [values.rules]);
  const params = useMemo(() => parseObject(values.activationParams, "activation parameters"), [values.activationParams]);
  const jsonError = ("error" in rules && rules.error) || ("error" in params && params.error) || undefined;

  const set = (patch: Partial<FormValues>) => setValues((v) => ({ ...v, ...patch }));

  const save = useMutation({
    mutationFn: () => {
      if ("error" in rules || "error" in params) throw new Error("unreachable: Save is disabled");
      const body: PutMeasurementPresetInput = {
        expected_version: preset?.version ?? 0,
        name: values.name,
        description: values.description,
        rules: rules.value,
        export_format: values.exportFormat,
        min_ip_retention_days: Number(values.minIpRetentionDays),
        activation_params: params.value,
        allowed_tenants: tenantsOf(values.allowedTenants),
      };
      return putMeasurementPreset(token, values.presetId, body);
    },
    onSuccess: (saved) => {
      // La version publiée remplace la tête : l'éditeur repart d'elle (``key``).
      qc.setQueryData(presetKeys.detail(saved.presetId), saved);
      void qc.invalidateQueries({ queryKey: presetKeys.list });
      void qc.invalidateQueries({ queryKey: presetKeys.history(saved.presetId) });
      if (isNew) {
        markSaved();
        guard.leave(`${LIST}/${encodeURIComponent(saved.presetId)}`);
      }
    },
  });

  const stale = !isNew && save.error instanceof ApiError && save.error.status === 412;
  // Recharger abandonne la saisie : demandé d'abord, comme toute autre sortie.
  const reload = () => requestLeave(() => qc.invalidateQueries({ queryKey: presetKeys.detail(values.presetId) }));

  return (
    <form
      onSubmit={(e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        // Entrée dans un champ soumet le formulaire même quand le bouton est désactivé.
        if (!dirty || jsonError) return;
        save.mutate();
      }}
    >
      <div className="card space-y-5">
        {isNew && (
          <Field label="Preset ID" help="Lowercase letters, digits and dashes, starting with a letter (2 to 32 characters). Cannot be changed later.">
            <Input
              mono
              required
              pattern={PRESET_ID_PATTERN}
              value={values.presetId}
              onChange={(e) => set({ presetId: e.target.value })}
            />
          </Field>
        )}
        <Field label="Name">
          <Input required maxLength={80} value={values.name} onChange={(e) => set({ name: e.target.value })} />
        </Field>
        <Field label="Description">
          <Textarea rows={2} maxLength={500} value={values.description} onChange={(e) => set({ description: e.target.value })} />
        </Field>
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Export format">
            <Select
              options={FORMAT_OPTIONS}
              value={values.exportFormat}
              onChange={(e) => set({ exportFormat: e.target.value as FormValues["exportFormat"] })}
            />
          </Field>
          <Field
            label="Minimum IP retention (days)"
            help="A stage that activates this preset must keep IP addresses at least this long (15 to 90 days)."
          >
            <Input
              type="number"
              required
              min={15}
              max={90}
              step={1}
              value={values.minIpRetentionDays}
              onChange={(e) => set({ minIpRetentionDays: e.target.value })}
            />
          </Field>
        </div>
        <Field label="Allowed tenants" help="Tenant ids, separated by commas. Leave empty to offer the preset to every tenant.">
          <Input mono value={values.allowedTenants} placeholder="All tenants" onChange={(e) => set({ allowedTenants: e.target.value })} />
        </Field>
        <Field
          label="Rules (JSON)"
          help="The complete rule set: every rule is required. Errors reported by the API name the rule by its path."
          error={"error" in rules ? rules.error : undefined}
        >
          <Textarea mono rows={24} spellCheck={false} value={values.rules} onChange={(e) => set({ rules: e.target.value })} />
        </Field>
        <Field
          label="Activation parameters (JSON)"
          help='Parameters a linked policy will provide, as {"name": {"type": "int", "min": 1, "max": 999999}}.'
          error={"error" in params ? params.error : undefined}
        >
          <Textarea
            mono
            rows={4}
            spellCheck={false}
            value={values.activationParams}
            onChange={(e) => set({ activationParams: e.target.value })}
          />
        </Field>
      </div>

      <FormActionBar
        dirty={dirty}
        submitLabel={isNew ? "Create preset" : "Save"}
        pending={save.isPending}
        disabled={!dirty || !!jsonError}
        disabledReason={jsonError ?? "Nothing to save"}
        onCancel={() => navigate(LIST)}
        error={
          <MutationError
            mutation={save}
            title="Could not save the preset"
            view={(error) => {
              const fields = fieldErrorsOf(error);
              if (!fields) return undefined;
              return (
                <Notice
                  tone="danger"
                  role="alert"
                  title="Could not save the preset"
                  actions={
                    <Button variant="ghost" size="sm" onClick={() => save.reset()}>
                      Dismiss
                    </Button>
                  }
                >
                  <ul className="mt-1 list-disc pl-5">
                    {fields.map((f) => (
                      <li key={`${f.field}\u0000${f.message}`}>
                        <span className="font-mono">{f.field}</span>: {f.message}
                      </li>
                    ))}
                  </ul>
                </Notice>
              );
            }}
          />
        }
      >
        {stale && <Button onClick={reload}>Reload latest version</Button>}
      </FormActionBar>
    </form>
  );
}

function History({ token, presetId }: { token: string; presetId: string }) {
  const [viewing, setViewing] = useState<number | null>(null);
  const query = useQuery({
    queryKey: presetKeys.history(presetId),
    queryFn: () => getMeasurementPresetHistory(token, presetId),
    enabled: !!token,
  });
  const version = useQuery({
    queryKey: presetKeys.version(presetId, viewing ?? 0),
    queryFn: () => getMeasurementPresetVersion(token, presetId, viewing ?? 0),
    enabled: !!token && viewing !== null,
  });
  const columns: TableColumn<MeasurementPresetVersion>[] = [
    { key: "version", header: "Version", mono: true, cell: (item) => String(item.version) },
    { key: "name", header: "Name", cell: (item) => item.name || "—" },
    { key: "author", header: "Author", cell: (item) => item.updatedBy || "—" },
    { key: "date", header: "Date", mono: true, muted: true, nowrap: true, cell: (item) => formatDateTime(item.updatedAt) },
    {
      key: "view",
      header: "Document",
      align: "right",
      cell: (item) => (
        <Button
          size="sm"
          variant="ghost"
          aria-label={`View version ${item.version}`}
          aria-pressed={viewing === item.version}
          onClick={() => setViewing(item.version)}
        >
          View
        </Button>
      ),
    },
  ];
  return (
    <section className="mt-8 space-y-4">
      <h2 className="text-sm font-semibold text-ink">History</h2>
      <RefreshFailed query={query} />
      <Table
        label="History"
        columns={columns}
        rows={query.data?.items}
        rowKey={(item) => String(item.version)}
        error={loadFailure(query)}
        empty={<EmptyState title="No history yet">Each published version of the preset appears here.</EmptyState>}
      />
      {viewing !== null &&
        (version.data ? (
          <Field label={`Version ${viewing} (read-only)`} help="A published version is never changed. To go back to it, copy its rules into the editor and save.">
            <Textarea mono readOnly rows={24} value={pretty(version.data)} />
          </Field>
        ) : (
          <Table
            label={`Version ${viewing}`}
            columns={[{ key: "doc", header: "Document", cell: () => null }]}
            rows={undefined}
            rowKey={() => ""}
            error={loadFailure(version)}
            empty={null}
          />
        ))}
    </section>
  );
}

export function MeasurementPresetEditPage() {
  const { presetId } = useParams<{ presetId: string }>();
  const isNew = !presetId;
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";

  const query = useQuery({
    queryKey: presetKeys.detail(presetId ?? ""),
    queryFn: () => getMeasurementPreset(token, presetId ?? ""),
    enabled: !!token && !isNew,
  });
  const preset = query.data;

  const header = (
    <PageHeader
      back={{ to: LIST, label: "Measurement presets" }}
      title={isNew ? "New measurement preset" : `Measurement preset ${presetId}`}
      subtitle={
        preset && (
          <>
            Version {preset.version}
            {preset.updatedBy ? ` · by ${preset.updatedBy}` : ""}
            {preset.updatedAt > 0 ? ` · ${formatDateTime(preset.updatedAt)}` : ""}
          </>
        )
      }
    />
  );

  if (isNew) {
    return (
      <Page width="form">
        {header}
        <Editor preset={null} token={token} />
      </Page>
    );
  }
  if (isLoadingOrPaused(query)) return <FormPlaceholder header={header} />;
  if (query.isLoadingError && isNotFound(query.error)) {
    return (
      <Page width="form">
        {header}
        <EmptyState title="No such preset">
          There is no measurement preset <span className="font-mono">{presetId}</span>.{" "}
          <Link to={LIST} className="underline">
            See every preset
          </Link>
        </EmptyState>
      </Page>
    );
  }
  if (!preset) return <FormPlaceholder header={header} failure={loadFailure(query)} />;
  return (
    <Page width="form">
      {header}
      <RefreshFailed query={query} className="mb-4" />
      {/* Remonté par version : une publication, ou un rechargement, repart de ce que tient le serveur. */}
      <Editor key={preset.version} preset={preset} token={token} />
      <History token={token} presetId={preset.presetId} />
    </Page>
  );
}
